import Lenis from 'lenis'
import { money } from '@/utils/money'
import { useFetch } from '@/utils/fetch'
import { getLenis } from '@/lib/lenis'
import { animate } from 'motion'
import ease from '@/utils/ease'

let cartLenis = null

const cartStore = {
	shopify: null,
	adding: false,
	success: false,
	loading: false,
	isOpen: false,
	inventoryCache: {} as Record<number, number>,
	fetchingInventory: new Set<number>(),

	init() {
		this.shopify = {
			items: [],
			item_count: 0,
			total_price: 0,
			currency: 'USD',
		}

		this.cartEl = document.querySelector('[data-cart-drawer]')

		this.handleCartOutsideClick = this.handleCartOutsideClick.bind(this)
		this.handleKeyUp = this.handleKeyUp.bind(this)

		window.addEventListener('keyup', this.handleKeyUp)

		this.load()
	},

	handleKeyUp(e) {
		if (e.key === 'Escape') {
			this.close()
		}
	},

	setShopify(cart) {
		this.shopify = cart
	},

	handleError(error) {
		return {
			success: false,
			error: error.message ?? 'Something went wrong.',
		}
	},

	handleSuccess() {
		return {
			success: true,
			cart: this.shopify,
		}
	},

	open() {
		this.isOpen = true

		getLenis()?.stop()

		if ((window.Alpine?.store('site') as any)?.menuOpen) {
			;(window.Alpine.store('site') as any).closeMenu()
		}

		if ((window.Alpine?.store('site') as any)?.mobileSearchOpen) {
			;(window.Alpine.store('site') as any).closeMobileSearch()
		}

		if (this.cartEl) {
			const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			animate(
				this.cartEl,
				{ transform: 'translateX(0)' },
				{
					duration: reduced ? 0 : 0.3,
					easing: ease.outQuint,
				},
			)
		}

		requestAnimationFrame(() => {
			this.setupCartListeners()
			this.initCartLenis()
		})
	},

	close() {
		this.isOpen = false

		this.destroyCartLenis()
		getLenis()?.start()

		if (this.cartEl) {
			const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			animate(
				this.cartEl,
				{ transform: 'translateX(100%)' },
				{
					duration: reduced ? 0 : 0.3,
					easing: ease.outQuint,
				},
			)
		}

		requestAnimationFrame(() => {
			this.removeCartListeners()
		})
	},

	toggle() {
		this.isOpen ? this.close() : this.open()
	},

	initCartLenis() {
		const wrapper = this.cartEl?.querySelector('.cart-drawer__lines')
		if (!wrapper || cartLenis) return

		cartLenis = new Lenis({
			wrapper,
			content: wrapper,
			smoothWheel: true,
			lerp: 0.1,
		})

		const raf = (time) => {
			if (cartLenis) {
				cartLenis.raf(time)
				requestAnimationFrame(raf)
			}
		}
		requestAnimationFrame(raf)
	},

	destroyCartLenis() {
		if (cartLenis) {
			cartLenis.destroy()
			cartLenis = null
		}
	},

	handleCartOutsideClick(event) {
		if (!this.cartEl.contains(event.target)) {
			this.close()
		}
	},

	setupCartListeners() {
		window.addEventListener('click', this.handleCartOutsideClick)
	},

	removeCartListeners() {
		window.removeEventListener('click', this.handleCartOutsideClick)
	},

	async load() {
		this.loading = true
		try {
			const cart = await useFetch('/cart.js', 'get')
			this.setShopify(cart)

			// Pre-fetch inventory for all cart items
			this.prefetchInventory()
		} catch (error) {
			return this.handleError(error)
		} finally {
			this.loading = false
			return this.handleSuccess()
		}
	},

	async prefetchInventory() {
		const items = this.shopify?.items || []
		for (const line of items) {
			await this.fetchInventory(line)
		}
	},

	async updateLines(lineItems = {}) {
		if (this.loading) return
		this.loading = true

		try {
			const cart = await useFetch('/cart/update.js', 'post', {
				updates: lineItems,
			})
			this.setShopify(cart)
		} catch (error) {
			return this.handleError(error)
		} finally {
			this.loading = false
			return this.handleSuccess()
		}
	},

	async addLines(lineItems = [], onClose = () => {}) {
		this.loading = true
		this.adding = true

		try {
			let payload

			if (lineItems.length === 1) {
				payload = await useFetch('/cart/add.js', 'post', lineItems[0])
			} else {
				payload = await useFetch('/cart/add.js', 'post', {
					items: lineItems,
				})
			}

			this.adding = false
			this.success = true
			this.open()

			setTimeout(() => {
				this.success = false
			}, 2000)

			onClose?.()

			return payload
		} catch (error) {
			return error
		} finally {
			this.load()
			return { status: 200, message: 'Added to cart' }
		}
	},

	async remove(item) {
		const itemKey = item.key || item.id
		const newItems = {
			[itemKey]: 0,
		}
		await this.updateLines(newItems)
	},

	async decrease(item) {
		const updatedQuantity = item.quantity - 1
		const itemKey = item.key || item.id
		const newItems = {
			[itemKey]: updatedQuantity,
		}
		await this.updateLines(newItems)
	},

	async increase(item) {
		const updatedQuantity = item.quantity + 1
		const itemKey = item.key || item.id
		const newItems = {
			[itemKey]: updatedQuantity,
		}
		await this.updateLines(newItems)
	},

	formatPrice(price) {
		const hasCents = price % 100 !== 0
		return money(price, hasCents, this.currency)
	},

	get currency() {
		return this.shopify?.currency
	},

	get count() {
		return this.shopify?.item_count ?? 0
	},

	get hasItems() {
		return (this.shopify?.item_count ?? 0) > 0
	},

	get totalPrice() {
		return money(this.shopify?.total_price || 0, false, this.currency)
	},

	get total() {
		return money(this.shopify?.total_price || 0, false, this.currency)
	},

	get cartLines() {
		const lines = this.shopify?.items || []

		return lines
	},

	get isLoading() {
		return this.loading
	},

	get isAdding() {
		return this.adding
	},

	getInventoryQuantity(line) {
		const variantId = line.variant_id || line.id

		// Return cached value if available
		if (this.inventoryCache[variantId] !== undefined) {
			return this.inventoryCache[variantId]
		}

		// Fetch inventory in the background
		this.fetchInventory(line)

		// Return a default that hides the buttons until we know the real value
		return 1
	},

	async fetchInventory(line) {
		const variantId = line.variant_id || line.id
		const handle = line.handle

		// Skip if no handle, already cached, or already fetching
		if (
			!handle ||
			this.inventoryCache[variantId] !== undefined ||
			this.fetchingInventory.has(variantId)
		) {
			return
		}

		// Mark as fetching
		this.fetchingInventory.add(variantId)

		try {
			const product = await useFetch(`/products/${handle}.js`, 'get')
			const variant = product.variants?.find((v) => v.id === variantId)

			if (variant) {
				this.inventoryCache[variantId] = variant.inventory_quantity ?? 1
			}
		} catch (error) {
			// Default to 1 on error (hides quantity buttons)
			this.inventoryCache[variantId] = 1
		} finally {
			this.fetchingInventory.delete(variantId)
		}
	},
}

export default cartStore
